const STORAGE_KEY = 'tese_form_auth';

const COOKIE_NAMES = {
  authToken: 'authtoken',
  token: 'token',
  userSid: 'user_sid',
  tenantId: 'tenant_id',
};

function getCookieDomain() {
  const fromEnv = (process.env.REACT_APP_COOKIE_DOMAIN || '').trim();
  if (fromEnv) return fromEnv;

  if (typeof window === 'undefined') return undefined;
  const hostname = window.location.hostname;
  if (hostname.includes('tese.io')) return '.tese.io';
  return undefined;
}

function readCookie(name) {
  if (typeof document === 'undefined') return '';
  const match = document.cookie.match(new RegExp(`(?:^|;\\s*)${name}=([^;]*)`));
  return match ? decodeURIComponent(match[1]) : '';
}

function writeCookie(name, value) {
  if (typeof document === 'undefined' || !value) return;
  document.cookie = serializeCookie(name, value, {
    domain: getCookieDomain(),
    secure: typeof window !== 'undefined' && window.location.protocol === 'https:',
  });
}

function deleteCookie(name) {
  if (typeof document === 'undefined') return;
  const domain = getCookieDomain();
  const expire = `${name}=; Path=/; Max-Age=0`;
  document.cookie = expire;
  if (domain) {
    document.cookie = `${expire}; Domain=${domain}`;
  }
}

function readStorage() {
  if (typeof window === 'undefined') return {};
  try {
    const raw = window.sessionStorage.getItem(STORAGE_KEY);
    return raw ? JSON.parse(raw) : {};
  } catch {
    return {};
  }
}

function writeStorage(session) {
  if (typeof window === 'undefined') return;
  try {
    window.sessionStorage.setItem(STORAGE_KEY, JSON.stringify(session));
  } catch {
    // private mode / blocked storage
  }
}

function clearStorage() {
  if (typeof window === 'undefined') return;
  try {
    window.sessionStorage.removeItem(STORAGE_KEY);
  } catch {
    // ignore
  }
}

/**
 * Serialize a first-party session cookie.
 * Uses RFC cookie flags (Secure, SameSite=Lax) so browsers actually persist them.
 */
export function serializeCookie(name, value, { domain, secure, sameSite = 'Lax', maxAgeDays = 7 } = {}) {
  const parts = [`${name}=${encodeURIComponent(value)}`, 'Path=/'];
  if (domain) parts.push(`Domain=${domain}`);
  if (secure) parts.push('Secure');
  parts.push(`SameSite=${sameSite}`);
  if (maxAgeDays) parts.push(`Max-Age=${Math.round(maxAgeDays * 86400)}`);
  return parts.join('; ');
}

export function persistSession({ authToken, token, userSid, tenantId } = {}) {
  const next = {
    authToken: authToken || '',
    token: token || '',
    userSid: userSid || '',
    tenantId: tenantId || '',
  };

  if (next.authToken) writeCookie(COOKIE_NAMES.authToken, next.authToken);
  if (next.token) writeCookie(COOKIE_NAMES.token, next.token);
  if (next.userSid) writeCookie(COOKIE_NAMES.userSid, next.userSid);
  if (next.tenantId) writeCookie(COOKIE_NAMES.tenantId, next.tenantId);

  writeStorage({
    ...readStorage(),
    ...Object.fromEntries(Object.entries(next).filter(([, value]) => Boolean(value))),
  });
}

export function readSession() {
  const stored = readStorage();
  return {
    authToken: stored.authToken || readCookie(COOKIE_NAMES.authToken) || '',
    token: stored.token || readCookie(COOKIE_NAMES.token) || '',
    userSid: stored.userSid || readCookie(COOKIE_NAMES.userSid) || '',
    tenantId: stored.tenantId || readCookie(COOKIE_NAMES.tenantId) || '',
  };
}

/**
 * A usable form-creator session is a JWT — `token` from SSO / v3 login,
 * or the wrapped `authtoken` from password login. user_sid is leftover v1 session.
 */
export function hasAuthSession(session = readSession()) {
  return Boolean(session.authToken || session.token);
}

export function buildAuthHeaders(session = readSession()) {
  const headers = {};
  if (session.authToken) headers.Authtoken = session.authToken;
  if (session.token) headers.token = session.token;
  if (session.tenantId) headers.tenant_id = session.tenantId;
  return headers;
}

export function applyIncomingCredentials(payload = {}) {
  const authToken = payload.authToken || payload.auth_token || '';
  const token = payload.token || '';
  const userSid = payload.userSid || payload.user_sid || '';
  const tenantId = payload.tenantId || payload.tenant_id || '';

  persistSession({ authToken, token, userSid, tenantId });
  return readSession();
}

export function applyUrlSearchParams(search = typeof window !== 'undefined' ? window.location.search : '') {
  const params = new URLSearchParams(search);
  const incoming = {
    authToken: params.get('auth_token') || '',
    token: params.get('token') || '',
    userSid: params.get('user_sid') || '',
    tenantId: params.get('tenant_id') || '',
  };

  if (incoming.authToken || incoming.token || incoming.userSid || incoming.tenantId) {
    persistSession(incoming);
  }

  return incoming;
}

export function stripAuthParamsFromUrl() {
  if (typeof window === 'undefined') return;
  const url = new URL(window.location.href);
  ['auth_token', 'token', 'user_sid', 'tenant_id'].forEach((key) => url.searchParams.delete(key));
  window.history.replaceState({}, '', url);
}

export function clearSession({ cookies = true, storage = true } = {}) {
  if (cookies) {
    Object.values(COOKIE_NAMES).forEach(deleteCookie);
  }
  if (storage) {
    clearStorage();
  }
}

export function getDashboardLoginUrl(returnTo) {
  const base = (process.env.REACT_APP_DASHBOARD_URL || '').replace(/\/$/, '');
  if (!base) return '';
  const loginUrl = new URL('/login', `${base}/`);
  if (returnTo) loginUrl.searchParams.set('return_to', returnTo);
  return loginUrl.toString();
}

export function requestParentAuth() {
  if (typeof window === 'undefined' || window.parent === window) {
    return Promise.resolve(hasAuthSession());
  }

  return new Promise((resolve) => {
    const finish = () => {
      window.removeEventListener('message', onMessage);
      resolve(hasAuthSession());
    };

    const onMessage = (event) => {
      if (!event?.data || event.data.type !== 'TESE_AUTH') return;
      applyIncomingCredentials(event.data.payload || {});
      clearTimeout(timer);
      finish();
    };

    const timer = setTimeout(finish, 1500);
    window.addEventListener('message', onMessage);
    window.parent.postMessage({ type: 'TESE_FORM_AUTH_REQUEST' }, '*');
  });
}
